import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { Formik, Form, useField } from "formik";

import SelectField from "@components/select-field";
import TextField from "@components/text-field";
import SliderField from "@components/slider-field";
import { MoneyInput, NumberInput, PercentInput } from "@components/mask-components";
import { ICurrency } from "shared/models/interfaces/response/realestate.interface";
import { useTranslations } from "next-intl";
import { parse } from "path";
import RangeSlider from "@components/range-slider-field"

const loanDurationOptions = [
  { value: 10, label: "10 años" },
  { value: 15, label: "15 años" },
  { value: 20, label: "20 años" },
  { value: 25, label: "25 años" },
  { value: 30, label: "30 años" },
  { value: 35, label: "35 años" },
  { value: 40, label: "40 años" },
];


function LoanCalculator(props: ILoanCalculator) {

  const AmountDataLabel = (props: any) => {
    return (
      <div className={"amountDataLabel amountDataLabel--" + props.data.classModifier}>
        <span className="amountDataLabel__title">{props.data.title}</span>
        <span className="amountDataLabel__amount" >{props.data.prefix + parseInt(props.data.amount).toLocaleString(undefined, {maximumFractionDigits:2})}</span>
        <span className="amountDataLabel__percent">{translation("percent") + ` ${props.data.percent}%`}</span>
      </div>
    );
  }

  const handleSubmit = (values: IFormValues) => {
    if (props.onSubmit) {
      props.onSubmit(values);
    }
  };

  const translation = useTranslations("mortgageCalculator.calculatorForm");

  const title = translation("title") || "";

  const [first, ...rest] = title.split(" ");

  const {
    initialPrice = "",
    currency = { iso: "RD", symbol: "RD" },
    disableLoanAmount = false,
  } = props;

  const initialPay = parseInt(initialPrice) * 0.3;

  const defaultValues: IFormValues = {
    initialPayment: initialPay.toString(),
    loanAmount: (parseInt(initialPrice) - initialPay).toString(),
    bankRate: "10.20",
    loanDuration: 20,
  };

  return (
    <>
      <Title>
        {first} <span>{rest.join(" ")}</span>
      </Title>
      <Formik enableReinitialize={true} initialValues={defaultValues} onSubmit={handleSubmit}>
        {(formikProps) => {
          const { bankRate, loanDuration, initialPayment, loanAmount } =
            formikProps.values;
 
          const loanAmountValue =
            parseFloat(initialPrice || loanAmount) -
            parseFloat(initialPayment || "0");

          const monthlyBankRate = parseFloat(bankRate) / 12 / 100;
          const duration = parseInt(loanDuration) * 12;

          let loan = (
            (loanAmountValue * monthlyBankRate) /
            (1 - Math.pow(1 + monthlyBankRate, -1 * duration))
          ).toFixed(2);
          
          let initialPaymentPercent = (parseFloat(initialPayment) * 100) / parseFloat(initialPrice);
          if(initialPaymentPercent > 100.00) {
            initialPaymentPercent = 100;
          }
          initialPaymentPercent = parseFloat(initialPaymentPercent.toFixed(0));

          let loanAmountPercent = (
            (loanAmountValue * 100) / parseFloat(initialPrice)
          ).toLocaleString(undefined, {maximumFractionDigits:0});

          let dataAmountLabel = {
            initialPay: {
              classModifier: 'initial',
              title: translation("initialPayment"),
              percent: isNaN(initialPaymentPercent) ? 0 : initialPaymentPercent,
              amount: initialPayment != '' ? initialPayment : '0',
              prefix: currency.symbol + "$", 
            },
            loanAmount: {
              classModifier: 'amount',
              title: translation("loanAmount"),
              percent: (loanAmountPercent == 'NaN' || parseInt(loanAmountPercent) < 0) ? 0 : loanAmountPercent,
              amount: loanAmountValue > 0 ? loanAmountValue : 0,
              prefix: currency.symbol + "$", 
            },
            minValue: parseInt(initialPrice) * 0.2,
            maxValue: parseInt(initialPrice) * 0.8,
            defaultValues: initialPay
          }

          return (
            <Wrapper>
              <InputWrapper>
                <TextField
                  name="bankRate"
                  label={translation("bankRate")}
                  inputComponent={PercentInput}
                  placeholder="Ej: % 12.95"
                />
              </InputWrapper>
              <InputWrapper>
                <SelectField
                  name="loanDuration"
                  label={translation("loanDuration")}
                  items={loanDurationOptions}
                />
              </InputWrapper>
              <InputRangeWrapper>
                <RangeLabelLeft>{translation("initialPayment")}</RangeLabelLeft>
                <RangeLabelRight>{translation("loanAmount")}</RangeLabelRight>
                <RangeSlider
                  onChange={(e) => {}}
                  value={dataAmountLabel.defaultValues}
                  name="initialPayment"
                  min={dataAmountLabel.minValue} 
                  max={dataAmountLabel.maxValue} 
                  defaultValue={dataAmountLabel.defaultValues}
                  initialPaymentPercent={dataAmountLabel.initialPay.percent}
                />
              </InputRangeWrapper>
              
                {disableLoanAmount && (
                  <LoanAmount
                    type="hidden"
                    name="loanAmount"
                    inputComponent={MoneyInput}
                    disabled={disableLoanAmount}
                    value={loanAmountValue > 0 ? loanAmountValue : 0}
                  />
                )}
                {!disableLoanAmount && (
                  <LoanAmount
                    type="hidden"
                    name="loanAmount"
                    inputComponent={MoneyInput}
                  />
                )}

              <InputWrapper>
                <AmountDataLabel data={dataAmountLabel.initialPay} />
              </InputWrapper>
              <InputWrapper>
                <AmountDataLabel data={dataAmountLabel.loanAmount} />
              </InputWrapper>
              <Footer>
                <Result finalAmount={loan}>
                  {translation("estimate")}
                  <Amount>
                    <MoneyInput
                      value={parseFloat(loan) > 0 ? loan : 0}
                      prefix={currency.symbol + "$"}
                      displayType="text"
                    />
                  </Amount>
                </Result>
                <SubmitButton type="submit" className="solicitar-prestamo-hipotecario">
                  {translation("request")}
                </SubmitButton>
              </Footer>
              <Details>
                <DetailsText>{translation("description")}</DetailsText>
                <DetailsText>{translation("info")}</DetailsText>
              </Details>
            </Wrapper>
          );
        }}
      </Formik>
    </>
  );
}

const sharedCss = css`
  display: flex;
  align-items: center;
  font-weight: 700;
  color: var(--color-brand-bg-secondary);
`;
const rangeLabelSharedCss = css`
  font-size: 13px;
  color: var(--color-brand-grey-main);
  font-weight: bold;
  margin-bottom: 15px;
`;

const Wrapper = styled(Form)`
  margin: 40px auto 0;
  display: flex;
  max-width: 800px;
  justify-content: space-between;
  flex-wrap: wrap;
`;

const InputWrapper = styled.div`
  width: 45%;
  margin-bottom: 20px;
  min-width: 45%;
  & .amountDataLabel {
    width: 100%;
    padding: 20px 5px 20px 10px;
    border-radius: 15px;
    color: #fff;
    & span {
      display: block;
    }
    &--amount,
    &--initial {
      margin-bottom: 30px;
      &::before {
        content: "";
        width: 56px;
        height: 70px;
        display: block;
        float: left;
        margin-right: 10px;
        background-position: center;
        background-size: 80%;
        background-repeat: no-repeat
      }
    }
    &--initial {
      background-color: var(--color-brand-blue-main);
      &::before {
        background-image: url(/icons/money_icon_remax.svg);
      }
    }
    &--amount {
      background-color: #9E9FA3;
      &::before {
        background-image: url(/icons/bank_icon_remax.svg)
      }
    }
    &__title {
      font-weight: bold;
      font-size: 16px;
    }
    &__amount {
      font-weight: bold;
      font-size: 28px;
      margin-bottom: 5px;
    }
    &__percent {
      font-size: 16px;
      font-style: italic;
    }
  }
  @media screen and (max-width: 868px) {
    & .amountDataLabel {
      &__amount {
        font-size: 26px;
      }
    }
  }
  @media screen and (max-width: 767px) {
    width: 100%;
    & .amountDataLabel {
      max-width: 450px;
      margin: 0 auto;
      &__title,
      &__percent {
        font-size: 14px;
      }
    }
  }
  @media screen and (max-width: 375px) {
    & .amountDataLabel {
      &__amount {
        font-size: 22px;
      }
    }
  }
`;
const InputRangeWrapper = styled.div`
  width: 100%;
  margin: 0 0 20px;
  padding: 0 5px;
`;

const RangeLabelLeft = styled.span`
  ${rangeLabelSharedCss}
`;

const RangeLabelRight = styled.span`
  ${rangeLabelSharedCss}
  float: right;
`;

const Footer = styled.div`
  ${sharedCss}
  width: 100%;
  margin-bottom: 50px;

  @media screen and (max-width: 767px) {
    flex-direction: column;
  }
`;

const Result = styled.div<IResultProps>`
  ${sharedCss}
  font-size: 1.1rem;
  width: 75%;
  background-color:var(--color-brand-red-main);
  // height: 80px;
  justify-content: center;
  border-radius: 50px;
  position: relative;

  ${(props) => {
    if (props.finalAmount.toString().length >= 7) {
      return css`
        flex-direction: column;
        text-align: center;
        ${Amount} {
          margin: 0;
        }
      `;
    }
  }}

  @media screen and (max-width: 767px) {
    font-size: 1rem;
    padding: 20px 0 10px;
    height: auto;
    width: 100%;
    flex-direction: column;
  }
`;

const SubmitButton = styled.button`
  ${sharedCss}
  background-color: var(--color-brand-blue-tertiary);
  font-size: 1.1rem;
  font-weight: 700;
  width: 30%;
  height: 60px;
  border-radius: 50px;
  justify-content: center;
  margin-left: -6%;
  border: none;
  /* outline: none; */
  cursor: pointer;
  font-family: var(--font-family);
  line-height: 1.15;

  @media screen and (max-width: 767px) {
    margin: 0;
    margin-top: 20px;
    width: 200px;
  }
`;

const LoanAmount = styled(TextField)`
  display: none;  
`;

const Amount = styled.span`
  font-size: 2.9rem;
  font-weight: 700;
  color: var(--color-brand-bg-secondary);
  margin-left: 30px;

  @media screen and (max-width: 767px) {
    font-size: 1.9rem;
    margin-left: 0;
    margin-top: 5px;
  }
`;

const Details = styled.div`
  text-align: center;
  color: var(--color-brand-grey-main);
`;

const DetailsText = styled.p`
  font-size: 0.7rem;
  color: var(--color-brand-grey-main);
  font-weight: 400;
  margin-bottom: 50px;

  &:first-of-type {
    font-size: 0.8rem;
    font-weight: 500;
  }
`;

const Title = styled.h2`
  font-size: 28.8px;
  color: black;
  margin-bottom: 1.5rem;

  & > span {
    font-weight: 300;
  }
`;

interface ILoanCalculator {
  initialPrice?: string;
  currency?: ICurrency;
  disableLoanAmount?: boolean;
  onSubmit?: any;
}

interface IFormValues {
  bankRate: string;
  loanDuration: any;
  initialPayment: string;
  loanAmount: string;
}

interface IResultProps {
  finalAmount: string | number;
}

const initialValues = {
  bankRate: "",
  loanDuration: "",
  initialPayment: "",
  loanAmount: "",
};

export default LoanCalculator;
